/**
 * Per-subpage content for the Travel Hub — SINGLE SOURCE OF TRUTH.
 *
 * Each entry below powers a /travel-hub/{slug} page rendered by the shared
 * `pages/TravelHubSubPage.jsx` template. Adding a new Travel Hub topic
 * requires only:
 *   1. Append an entry to TRAVEL_HUB_CONTENT below.
 *   2. (Optional) Add a route in App.js (uses the same shared template).
 *   3. (Optional) Surface it on /travel-hub.
 *
 * Required fields:
 *   • slug              — URL segment under /travel-hub/{slug}
 *   • eyebrowIcon       — lucide-react icon name string for the hero pill
 *   • eyebrow           — short hero pill label (e.g. "Visa Support")
 *   • title             — H1
 *   • subtitle          — 1–2 sentence intro
 *   • disclaimer        — optional small note shown under the hero subtitle
 *   • helpsTitle        — section heading above the helps list
 *   • helps[]           — bullet list strings
 *   • audienceTitle     — section heading above the 3-card grid
 *   • audience[]        — { icon: lucide-name string, text }
 *   • steps[]           — { n, text } — exactly 3 entries for the "How it works"
 *   • ctaTitle          — final CTA section heading
 *   • ctaSubtitle       — supporting copy under the CTA heading
 *   • whatsappPrompt    — pre-filled WhatsApp message
 *   • metaDescription   — SEO meta description (optional)
 */

export const TRAVEL_HUB_CONTENT = {
  'visa-support': {
    slug: 'visa-support',
    eyebrowIcon: 'FileText',
    eyebrow: 'Visa Support',
    title: 'Visa Support for Your Healthcare Journey',
    subtitle:
      'We help make your trip to Vietnam smoother with simple visa guidance, entry support, and trusted coordination.',
    metaDescription:
      'Visa support for international patients travelling to Vietnam for healthcare — entry guidance and medical travel documentation, coordinated by VietClinics.',
    helpsTitle: 'What we help with',
    helps: [
      'Tourist visa guidance for Vietnam entry',
      'Medical travel support documentation',
      'Entry and document guidance',
      'Fast-track support through trusted partners',
      'Visa extension guidance when needed',
    ],
    audienceTitle: 'Who this is for',
    audience: [
      { icon: 'User',  text: 'International patients visiting Vietnam' },
      { icon: 'Users', text: 'Patients travelling for consultations or treatment' },
      { icon: 'Users', text: 'Family members accompanying patients' },
    ],
    steps: [
      { n: '01', text: 'Tell us your travel dates and nationality' },
      { n: '02', text: 'We guide you on the best visa path' },
      { n: '03', text: 'We coordinate next steps with our team or trusted partners' },
    ],
    ctaTitle: 'Need visa guidance before you travel?',
    ctaSubtitle: "Our team will guide you on every step of the visa process.",
    whatsappPrompt: 'Hi VietClinics! I need visa support guidance.',
  },

  transport: {
    slug: 'transport',
    eyebrowIcon: 'Car',
    eyebrow: 'Transport',
    title: 'Transport & Arrival Support',
    subtitle:
      'From airport pickup to clinic transfers, we help coordinate smooth and reliable transport for your healthcare journey.',
    metaDescription:
      'Reliable airport pickup, clinic transfers and private transport coordination for international patients travelling to Vietnam for treatment.',
    helpsTitle: 'What we help with',
    helps: [
      'Airport pickup coordination',
      'Hotel to clinic transfers',
      'Return trip planning',
      'Private car options through trusted partners',
      'Support for patients travelling with companions',
    ],
    audienceTitle: 'Ideal for',
    audience: [
      { icon: 'MapPin', text: 'First-time visitors to Vietnam' },
      { icon: 'Users',  text: 'Patients with appointments in multiple locations' },
      { icon: 'User',   text: 'Patients who want a smooth and stress-free arrival' },
    ],
    steps: [
      { n: '01', text: 'Share your arrival details' },
      { n: '02', text: 'We recommend the best transport option for your journey' },
      { n: '03', text: 'Our team helps coordinate the journey end-to-end' },
    ],
    ctaTitle: 'Need help planning your arrival?',
    ctaSubtitle: "Tell us your dates and we'll take care of the rest.",
    whatsappPrompt: 'Hi VietClinics! I need transport / arrival support.',
  },

  'stay-recovery': {
    slug: 'stay-recovery',
    eyebrowIcon: 'BedDouble',
    eyebrow: 'Stay & Recovery',
    title: 'Stay & Recovery Guidance',
    subtitle:
      'We recommend trusted hotels and recovery-friendly accommodation near your clinic, based on comfort, convenience, and location.',
    disclaimer: 'Guidance & recommendations only — not a booking service',
    metaDescription:
      'Hotel and recovery-stay recommendations for international patients receiving treatment in Vietnam — comfortable, clinic-close options curated by VietClinics.',
    helpsTitle: 'What we help with',
    helps: [
      'Hotel recommendations near clinics',
      'Recovery-friendly stays in comfortable locations',
      'Convenient locations for follow-up appointments',
      'Accommodation suggestions for short or longer stays',
      'Guidance for patients travelling with family or companions',
    ],
    audienceTitle: 'Best for',
    audience: [
      { icon: 'BedDouble', text: 'Patients having treatment over multiple days' },
      { icon: 'User',      text: 'Patients who want convenient recovery stays' },
      { icon: 'Users',     text: 'International visitors needing help choosing the right area' },
    ],
    steps: [
      { n: '01', text: 'Tell us your treatment city and dates' },
      { n: '02', text: 'We recommend suitable stay options based on your needs' },
      { n: '03', text: 'You choose what works best for you' },
    ],
    ctaTitle: 'Need help choosing where to stay?',
    ctaSubtitle: "Tell us your clinic and dates — we'll recommend the best options.",
    whatsappPrompt: 'Hi VietClinics! I need stay & recovery recommendations.',
  },

  'medical-packages': {
    slug: 'medical-packages',
    eyebrowIcon: 'Package',
    eyebrow: 'Medical Packages',
    title: 'Medical Travel Packages',
    subtitle:
      'We help coordinate treatment support packages that may include clinic guidance, transport support, and stay recommendations.',
    disclaimer: 'Coordinated guidance — no checkout, no fixed pricing',
    metaDescription:
      'Bundled medical travel coordination for international patients visiting Vietnam — clinic shortlisting, transport and stay recommendations from VietClinics.',
    helpsTitle: 'What may be included',
    helpsFooter:
      'Exact support depends on your treatment needs and travel preferences. Our team will discuss the right options for you.',
    helps: [
      'Clinic guidance and shortlisting',
      'Travel planning support',
      'Airport pickup coordination',
      'Stay recommendations near your clinic',
      'Support before arrival and during treatment',
    ],
    audienceTitle: 'Best for',
    audience: [
      { icon: 'User',  text: 'Patients wanting a more guided journey' },
      { icon: 'Globe', text: 'International patients unfamiliar with Vietnam' },
      { icon: 'Users', text: 'Patients who want one point of contact for everything' },
    ],
    steps: [
      { n: '01', text: 'Tell us what treatment you need' },
      { n: '02', text: 'We suggest suitable clinic and support options' },
      { n: '03', text: 'We coordinate the next steps with you' },
    ],
    ctaTitle: 'Looking for a more supported healthcare journey?',
    ctaSubtitle: 'Our team will personalise the right support package for your needs.',
    whatsappPrompt: 'Hi VietClinics! I would like a medical travel package.',
  },
};

export const getTravelHubContent = (slug) => TRAVEL_HUB_CONTENT[slug] || null;

export const ALL_TRAVEL_HUB_SUBPAGES = Object.values(TRAVEL_HUB_CONTENT);
